/**
 * SimpleList — 通用列表组件 (flex 行布局 + 前端分页)
 * 替代 antd Table，每行支持 media / title / description / extra 四个区域
 */
import { makeStyles, tokens, Spinner } from '@fluentui/react-components'
import type { ReactNode } from 'react'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    transition: 'background-color 0.1s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  rowNoClick: {
    cursor: 'default',
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  media: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
  },
  content: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  title: {
    fontWeight: 600,
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  desc: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  extra: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '40px 20px',
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 0',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 0',
  },
})

export interface SimpleListItem {
  key: string
  media?: ReactNode
  title: ReactNode
  description?: ReactNode
  extra?: ReactNode
  onClick?: () => void
}

interface SimpleListProps {
  items: SimpleListItem[]
  loading?: boolean
  emptyText?: string
}

export default function SimpleList({
  items,
  loading = false,
  emptyText = '暂无数据',
}: SimpleListProps) {
  const styles = useStyles()

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="medium" />
      </div>
    )
  }

  if (items.length === 0) {
    return <div className={styles.empty}>{emptyText}</div>
  }

  return (
    <div className={styles.root}>
      {items.map((item) => (
        <div
          key={item.key}
          className={item.onClick ? styles.row : `${styles.row} ${styles.rowNoClick}`}
          onClick={item.onClick}
        >
          {item.media && <div className={styles.media}>{item.media}</div>}
          <div className={styles.content}>
            <div className={styles.title}>{item.title}</div>
            {item.description && <div className={styles.desc}>{item.description}</div>}
          </div>
          {item.extra && <div className={styles.extra}>{item.extra}</div>}
        </div>
      ))}
    </div>
  )
}
